import { FC } from 'react';
import { Box, useTheme, useMediaQuery, Button, Typography, Link } from '@mui/material';
import footerBackgroundImage from '@assets/images/footer-bg.png';
import footerBackgroundMobileImage from '@assets/images/footer-bg-mob.png';
import { trackGAEvent } from '@utils/analytics';
import { SCHEDULE_CALL_LINK } from '@constants/constants';
import useNavigateToLogin from '@hooks/useNavigateToLogin';

const CalculatorFooter: FC = () => {
  const theme = useTheme();
  const { navigateToLogin } = useNavigateToLogin();
  const isSmallDevice = useMediaQuery(theme.breakpoints.down('md'));
  const is1250x = useMediaQuery('(max-width:1250px)');
  const is1100x = useMediaQuery('(max-width:1100px)');

  return (
    <Box
      id="calculator_landing_footer"
      sx={{
        width: '100%',
        background: theme.palette.funding_calculator.landing_page.background,
        fontFamily: 'Manrope, sans-serif',
        position: 'sticky',
        bottom: 0,
        zIndex: 500,
        m: '0.75rem',
      }}
    >
      <Box
        sx={{
          width: 'calc(100% - 1.25rem)',
          height: isSmallDevice ? '50rem' : 'auto',
          position: 'relative',
          overflow: 'hidden',
          paddingBottom: isSmallDevice ? '0' : '37.07%',
          backgroundImage: isSmallDevice
            ? `url(${footerBackgroundMobileImage})`
            : `url(${footerBackgroundImage})`,
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          borderRadius: '1.5rem',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            zIndex: 15,
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            px: '2rem',
            pt: is1250x ? '1rem' : '5rem',
            pb: '1rem',
            color: 'white',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              width: '100%',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: isSmallDevice ? 'column' : 'row',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                width: '100%',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  width: '80%',
                }}
              >
                <Typography
                  fontFamily={'Manrope, sans-serif'}
                  fontSize={is1100x ? '2rem' : '3rem'}
                  fontWeight={700}
                  sx={{
                    color: '#F6F8F9',
                  }}
                >
                  Fuel your game's growth with non-dilutive,
                  {!is1250x ? <br /> : <></>} user acquisition financing
                </Typography>
                <Typography
                  fontFamily={'Manrope, sans-serif'}
                  fontSize={is1100x ? '0.8rem' : '1rem'}
                  fontWeight={400}
                  sx={{
                    color: '#F6F8F9',
                  }}
                >
                  Get the funds you need to boost growth with a simple credit line for user
                  acquisition
                </Typography>
              </Box>
              {isSmallDevice && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                    width: '100%',
                    mt: '1rem',
                  }}
                >
                  <Button
                    sx={{
                      my: isSmallDevice ? 0 : '1.5rem',
                      mr: '0.5rem',
                      maxWidth: '15rem',
                      background: '#F6F8F9',
                      borderRadius: '100px',
                      padding: '14px 24px',
                      color: '#101112',
                      letterSpacing: '-3%',
                      fontSize: isSmallDevice ? '0.8rem' : '1rem',
                      lineHeight: '15px',
                      fontWeight: 600,
                      textTransform: 'none',
                      fontFamily: 'Manrope, sans-serif',
                      '&:hover': {
                        backgroundColor: '#F6F8F9',
                      },
                    }}
                    href={SCHEDULE_CALL_LINK}
                    target="_blank"
                    onClick={() => {
                      trackGAEvent({
                        action: 'schedule_call',
                        category: 'funding_calculator',
                        landing_page: window.location.href,
                        location: 'Footer',
                      });
                    }}
                  >
                    Contact Us
                  </Button>
                  <Button
                    sx={{
                      my: isSmallDevice ? 0 : '1.5rem',
                      ml: '0.5rem',
                      maxWidth: '15rem',
                      background: '#F6F8F910',
                      borderRadius: '100px',
                      padding: '14px 24px',
                      color: theme.palette.common.white,
                      letterSpacing: '-3%',
                      fontSize: isSmallDevice ? '0.8rem' : '1rem',
                      lineHeight: '15px',
                      fontWeight: 600,
                      textTransform: 'none',
                      fontFamily: 'Manrope, sans-serif',
                      '&:hover': {
                        backgroundColor: '#F6F8F910',
                      },
                    }}
                    onClick={navigateToLogin}
                  >
                    Sign In
                  </Button>
                </Box>
              )}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: isSmallDevice ? 'space-between' : 'center',
                  width: isSmallDevice ? '100%' : '20%',
                  marginTop: isSmallDevice ? '7rem' : '1rem',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    textAlign: isSmallDevice ? 'center' : 'flex-start',
                    width: '50%',
                  }}
                >
                  <Typography
                    fontFamily={'Manrope, sans-serif'}
                    fontSize={'0.5rem'}
                    fontWeight={700}
                    sx={{
                      color: '#989CA2',
                    }}
                  >
                    FUNDING
                  </Typography>
                  <Typography
                    fontFamily={'Manrope, sans-serif'}
                    fontSize={'0.9rem'}
                    fontWeight={700}
                    sx={{
                      color: '#F6F8F9',
                      my: '0.5rem',
                    }}
                  >
                    PvX Capital
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    textAlign: isSmallDevice ? 'center' : 'flex-start',
                    width: '50%',
                  }}
                >
                  <Typography
                    fontFamily={'Manrope, sans-serif'}
                    fontSize={'0.5rem'}
                    fontWeight={700}
                    sx={{
                      color: '#989CA2',
                    }}
                  >
                    COMPANY
                  </Typography>
                  <Typography
                    fontFamily={'Manrope, sans-serif'}
                    fontSize={'0.9rem'}
                    fontWeight={700}
                    sx={{
                      color: '#F6F8F9',
                      my: '0.5rem',
                    }}
                  >
                    About Us
                  </Typography>
                  <Typography
                    fontFamily={'Manrope, sans-serif'}
                    fontSize={'0.9rem'}
                    fontWeight={700}
                    sx={{
                      color: '#F6F8F9',
                      my: '0.5rem',
                    }}
                  >
                    Team
                  </Typography>
                </Box>
              </Box>
            </Box>
            {!isSmallDevice && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start',
                  width: '100%',
                }}
              >
                <Button
                  sx={{
                    my: isSmallDevice ? 0 : '1.5rem',
                    mr: '0.5rem',
                    maxWidth: '15rem',
                    background: '#F6F8F9',
                    borderRadius: '100px',
                    padding: '14px 24px',
                    color: '#101112',
                    letterSpacing: '-3%',
                    fontSize: isSmallDevice ? '0.8rem' : '1rem',
                    lineHeight: '15px',
                    fontWeight: 600,
                    textTransform: 'none',
                    fontFamily: 'Manrope, sans-serif',
                    '&:hover': {
                      backgroundColor: '#F6F8F9',
                    },
                  }}
                  href={SCHEDULE_CALL_LINK}
                  target="_blank"
                >
                  Contact Us
                </Button>
                <Button
                  sx={{
                    my: isSmallDevice ? 0 : '1.5rem',
                    ml: '0.5rem',
                    maxWidth: '15rem',
                    background: '#F6F8F910',
                    borderRadius: '100px',
                    padding: '14px 24px',
                    color: theme.palette.common.white,
                    letterSpacing: '-3%',
                    fontSize: isSmallDevice ? '0.8rem' : '1rem',
                    lineHeight: '15px',
                    fontWeight: 600,
                    textTransform: 'none',
                    fontFamily: 'Manrope, sans-serif',
                    '&:hover': {
                      backgroundColor: '#F6F8F910',
                    },
                  }}
                  onClick={navigateToLogin}
                >
                  Sign In
                </Button>
              </Box>
            )}
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: isSmallDevice ? 'column' : 'row',
              justifyContent: 'space-between',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: isSmallDevice ? 'center' : 'flex-start',
                mb: isSmallDevice ? '1rem' : 0,
              }}
            >
              <Link
                href="https://www.linkedin.com/company/pvx-partners"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ textDecoration: 'none' }}
              >
                <Typography
                  fontFamily={'Manrope, sans-serif'}
                  fontSize={'0.7rem'}
                  fontWeight={700}
                  sx={{
                    px: '0.5rem',
                    color: '#F6F8F9',
                    cursor: 'pointer',
                    '&:hover': {
                      opacity: 0.8,
                    },
                  }}
                >
                  Linkedin
                </Typography>
              </Link>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: isSmallDevice ? 'center' : 'flex-end',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1,
              }}
            >
              <Typography
                fontFamily={'Manrope, sans-serif'}
                fontSize={'0.7rem'}
                fontWeight={700}
                sx={{
                  px: '0.5rem',
                  color: '#989CA2B2',
                  opacity: 0.7,
                }}
              >
                © 2024 PvX PARTNERS PTE LTD
              </Typography>
              <Link
                href="https://pvxpartners.com/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ textDecoration: 'none' }}
              >
                <Typography
                  fontFamily={'Manrope, sans-serif'}
                  fontSize={'0.7rem'}
                  fontWeight={700}
                  sx={{
                    px: '0.5rem',
                    color: '#989CA2B2',
                    opacity: 0.7,
                    cursor: 'pointer',
                    '&:hover': {
                      opacity: 1,
                    },
                  }}
                >
                  PRIVACY POLICY
                </Typography>
              </Link>
              <Link
                href="https://pvxpartners.com/terms-of-use"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ textDecoration: 'none' }}
              >
                <Typography
                  fontFamily={'Manrope, sans-serif'}
                  fontSize={'0.7rem'}
                  fontWeight={700}
                  sx={{
                    px: '0.5rem',
                    color: '#989CA2B2',
                    opacity: 0.7,
                    cursor: 'pointer',
                    '&:hover': {
                      opacity: 1,
                    },
                  }}
                >
                  TERMS OF SERVICE
                </Typography>
              </Link>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CalculatorFooter;
